// The two Reports dropdowns, expressed over the {mode, month, year} the store
// already holds. No new state: a quarter is its last month, and a scope that
// needs no month still carries the one it was handed, so switching away and back
// returns you to the period you were reading.
//
// Presentation only — what each mode *means* is the backend's `in_period`, and
// these labels have to keep agreeing with it.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December']

export const SCOPES = [
    { value: 'month', label: 'Monthly' },
    { value: 'quarter', label: 'Quarterly' },
    { value: 'ytd', label: 'Year to Date' },
    { value: 'year', label: 'Year' },
    { value: 'all', label: 'All Time' },
]

// The last month of the quarter `month` falls in — the anchor a selected quarter
// is stored as. Q1 → March, Q2 → June, Q3 → September, Q4 → December.
const quarterAnchor = (month) => Math.floor(month / 3) * 3 + 2
const quarterOf = (month) => Math.floor(month / 3) + 1

// The earliest year there is something to report on, through this one.
//
// `firstYear` is not always a year — it is absent before the first response
// lands and null for an account with no won deals — so anything implausible is
// ignored rather than trusted; a stray 0 would build two thousand options.
// `selected` is always included: picking a salesperson re-derives first_year
// from their deals alone, which can drop the year already showing.
export const availableYears = (firstYear, selected, thisYear = new Date().getFullYear()) => {
    const usable = Number.isInteger(firstYear) && firstYear >= 1970 && firstYear <= thisYear
    const start = Math.min(usable ? firstYear : thisYear, Number.isInteger(selected) ? selected : thisYear)
    const end = Math.max(thisYear, Number.isInteger(selected) ? selected : thisYear)
    const years = []
    for (let y = end; y >= start; y -= 1) years.push(y)
    return years
}

// The second dropdown's contents, newest year first; within a year the order the
// month select already had. All Time returns nothing — it has no period to pick.
// Each option carries the store patch it applies, so the page never parses a key
// back into a date.
export const periodOptions = (mode, { firstYear, year, month }, thisYear = new Date().getFullYear()) => {
    if (mode === 'all') return []
    const years = availableYears(firstYear, year, thisYear)

    if (mode === 'year') {
        return years.map((y) => ({ value: String(y), label: String(y), patch: { year: y } }))
    }
    if (mode === 'quarter') {
        return years.flatMap((y) => [0, 1, 2, 3].map((q) => ({
            value: `${y}-Q${q + 1}`,
            label: `Q${q + 1} ${y}`,
            patch: { month: q * 3 + 2, year: y },
        })))
    }
    // Monthly and YTD both pick a month; it reads as the period or as its
    // endpoint.
    return years.flatMap((y) => MONTHS.map((m, i) => ({
        value: `${y}-${i}`,
        label: mode === 'ytd' ? `Through ${m} ${y}` : `${m} ${y}`,
        patch: { month: i, year: y },
    })))
}

// Which option is showing. Quarterly reads the quarter the anchor falls in, not
// the anchor itself, so a month carried in from another scope still matches an
// option instead of leaving the control blank.
export const selectedKey = (mode, year, month) => {
    if (mode === 'all') return ''
    if (mode === 'year') return String(year)
    if (mode === 'quarter') return `${year}-Q${quarterOf(month)}`
    return `${year}-${month}`
}

// What the period is called in the two chart subtitles and the Deals closed
// heading.
export const periodLabel = (mode, year, month) => {
    if (mode === 'all') return 'All Time'
    if (mode === 'year') return String(year)
    if (mode === 'quarter') return `Q${quarterOf(month)} ${year}`
    // Unchanged from before the other scopes existed, en dash and all.
    if (mode === 'ytd') return `Jan–${MONTHS[month]} ${year} YTD`
    return `${MONTHS[month]} ${year}`
}

// Moving between scopes carries the period across: February stays in Q1, Q1
// stays March, and a scope with no period of its own still keeps the anchor so
// going back restores it.
export const scopePatch = (mode, current) => ({
    mode,
    year: current.year,
    month: mode === 'quarter' ? quarterAnchor(current.month) : current.month,
})
